import { useState } from "react";
import SEO from "@/components/SEO";
import WaitlistForm from "@/components/WaitlistForm";
import { ASSETS } from "@/lib/api";
import { Calendar, BookOpen, Award, Plus, Minus } from "lucide-react";

const FAQS = [
    {
        q: "When is Purposeful, Inc. launching?",
        a: "The book launches in December 2027. Waitlist members get the opening chapter as a free PDF, monthly founder interview snippets, and buy-access three months before the public.",
    },
    {
        q: "What is the book about?",
        a: "Purposeful, Inc. is a curated set of lessons from 100 impact founders building businesses that matter. It's structured across 12 chapters covering origin stories, hard tradeoffs, capital, culture and scaling with conviction.",
    },
    {
        q: "Who is Chandrashekar Bhat?",
        a: "Chandrashekar Bhat is the author of Purposeful, Inc. and the founder of The Purposeful Network. He's an operator, writer and convener working at the intersection of entrepreneurship and sustainability.",
    },
    {
        q: "How can my venture be featured in the book or on the Enterprises directory?",
        a: "Head to the Directory page and click 'Get listed', or write directly to purposeful.inc@gmail.com with a short description of your venture, the founder, and your impact thesis.",
    },
    {
        q: "How do I get early access?",
        a: "Join the waitlist from any page on this site. Waitlist members receive the first chapter as a private PDF, unpublished founder interview snippets and priority ordering ahead of the December 2027 launch.",
    },
];

export default function AboutBook() {
    const [openFaq, setOpenFaq] = useState(0);
    return (
        <main data-testid="book-page" className="text-white pt-32 pb-24">
            <SEO
                title="The Book | Purposeful, Inc. by Chandrashekar Bhat | Launching Dec 2027"
                description="Purposeful, Inc. is a hardcover book featuring lessons from 100 impact founders. Written by Chandrashekar Bhat and launching December 2027. Explore chapters and join the waitlist."
                path="/book"
                keywords="Purposeful Inc book, impact entrepreneurs book, Chandrashekar Bhat book, book on impact founders, book launch 2027, business books, social entrepreneurship book"
                schema={[
                    {
                        "@context": "https://schema.org",
                        "@type": "Book",
                        "name": "Purposeful, Inc.: Building Businesses That Matter",
                        "bookFormat": "https://schema.org/Hardcover",
                        "numberOfPages": 340,
                        "author": { "@type": "Person", "name": "Chandrashekar Bhat", "sameAs": "https://www.linkedin.com/in/buildwithbhat/" },
                        "publisher": { "@id": "https://purposeful.inc/#organization" },
                        "datePublished": "2027-12-01",
                        "inLanguage": "en",
                        "description": "Lessons from 100 impact founders. The origin stories, hard tradeoffs, and convictions behind ventures reshaping how we live and work.",
                        "image": "https://purposeful.inc/assets/book-cover.png",
                        "url": "https://purposeful.inc/book"
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": FAQS.map((f) => ({
                            "@type": "Question",
                            "name": f.q,
                            "acceptedAnswer": { "@type": "Answer", "text": f.a },
                        })),
                    },
                ]}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "The Book", path: "/book" }]}
            />

            <section className="container-x mx-auto max-w-7xl grid lg:grid-cols-2 gap-16 items-center">
                <div className="relative mx-auto max-w-md w-full">
                    <img src={ASSETS.bookCover} alt="Purposeful, Inc." loading="eager" decoding="async" fetchpriority="high" className="relative w-full" data-testid="book-cover-large" />
                </div>
                <div>
                    <div className="overline mb-4">The Book</div>
                    <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight">
                        100 founders.<br />
                        <span className="text-[#D4AF37]">One question:</span> what is worth building?
                    </h1>
                    <p className="mt-6 text-zinc-300 text-lg leading-relaxed">
                        Purposeful, Inc. is a deep, on-the-record look at the
                        founders quietly defining the next decade of
                        entrepreneurship. Those building ventures where
                        commerce and consequence are inseparable.
                    </p>
                    <div className="mt-8 space-y-3 text-sm">
                        <div className="flex items-center gap-3 text-zinc-300"><Calendar size={16} className="text-[#D4AF37]" /> Launching December 2027</div>
                        <div className="flex items-center gap-3 text-zinc-300"><BookOpen size={16} className="text-[#D4AF37]" /> Hardcover · ~340 pages · 12 chapters</div>
                        <div className="flex items-center gap-3 text-zinc-300"><Award size={16} className="text-[#D4AF37]" /> Free PDF excerpt for waitlist members</div>
                    </div>
                    <div className="mt-9 max-w-md">
                        <WaitlistForm source="book-page" testIdPrefix="book-waitlist" compact />
                    </div>
                </div>
            </section>

            <section className="container-x mx-auto max-w-5xl mt-32">
                <div className="overline mb-4">What's inside</div>
                <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                    A field guide for the next generation of builders.
                </h2>
                <div className="mt-12 grid md:grid-cols-2 gap-6">
                    {[
                        { n: "01", t: "The origin of purpose", d: "Why these founders started, and what they nearly built instead." },
                        { n: "02", t: "Building uncomfortable teams", d: "Hiring against monoculture in an industry that rewards consensus." },
                        { n: "03", t: "Capital with conviction", d: "Raising money without renting your soul to growth-at-all-costs." },
                        { n: "04", t: "Measuring what matters", d: "Why traditional KPIs miss the point, and what to track instead." },
                        { n: "05", t: "The loneliness of mission", d: "Founder mental health when your customer's outcome is existential." },
                        { n: "06", t: "Scaling without selling out", d: "Hard cases where staying true cost a market, and where it built one." },
                    ].map((c) => (
                        <div key={c.n} className="card-dark p-8" data-testid={`chapter-card-${c.n}`}>
                            <div className="font-mono text-xs text-[#D4AF37]">CH. {c.n}</div>
                            <h3 className="font-display text-2xl mt-2">{c.t}</h3>
                            <p className="text-zinc-400 mt-3 text-sm leading-relaxed">{c.d}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="section-y border-t border-white/5" data-testid="book-faq-section">
                <div className="container-x mx-auto max-w-4xl">
                    <div className="overline mb-4">Frequently asked</div>
                    <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                        Answers before you<br />
                        <span className="text-[#D4AF37]">ask.</span>
                    </h2>

                    <div className="mt-12 divide-y divide-white/5 border-y border-white/5">
                        {FAQS.map((f, i) => {
                            const open = openFaq === i;
                            return (
                                <div key={i} className="py-5" data-testid={`faq-item-${i}`}>
                                    <button
                                        onClick={() => setOpenFaq(open ? -1 : i)}
                                        className="w-full flex items-center justify-between gap-6 text-left group"
                                        data-testid={`faq-toggle-${i}`}
                                        aria-expanded={open}
                                    >
                                        <span className="font-display text-lg md:text-xl text-white group-hover:text-[#D4AF37] transition">{f.q}</span>
                                        <span className="shrink-0 rounded-full border border-white/15 p-2 text-zinc-400 group-hover:border-[#D4AF37] group-hover:text-[#D4AF37] transition">
                                            {open ? <Minus size={14} /> : <Plus size={14} />}
                                        </span>
                                    </button>
                                    {open && (
                                        <p className="mt-4 text-zinc-400 text-sm md:text-base leading-relaxed max-w-3xl">
                                            {f.a}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="section-y border-t border-white/5 relative overflow-hidden mt-8">
                <div className="absolute inset-0 fingerprint-bg" />
                <div className="container-x mx-auto max-w-3xl text-center relative">
                    <div className="overline mb-4">Reserve your copy</div>
                    <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tight">
                        Read the first chapter<br />
                        before <span className="text-[#D4AF37]">the world does.</span>
                    </h2>
                    <p className="mt-6 text-zinc-400 text-lg">
                        Waitlist members get the opening chapter, founder
                        interviews, and early access before the December 2027
                        launch.
                    </p>
                    <div className="mt-8 max-w-lg mx-auto">
                        <WaitlistForm source="book-final-cta" testIdPrefix="book-final-waitlist" compact />
                    </div>
                </div>
            </section>
        </main>
    );
}
